import React, { useEffect, useMemo, useState } from "react";
import adminService from "../../services/adminService";

/* ---------------- helpers ---------------- */
const safe = (v, fallback = "-") => (v === null || v === undefined || v === "" ? fallback : v);

const pick = (obj, paths, fallback = null) => {
  for (const p of paths) {
    const v = p.split(".").reduce((acc, k) => (acc ? acc[k] : undefined), obj);
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return fallback;
};

const formatDate = (d) => {
  if (!d) return "-";
  try {
    return new Date(d).toLocaleString("fr-FR");
  } catch {
    return String(d);
  }
};

// ✅ API base (pour images / fallback)
const API_BASE =
  process.env.REACT_APP_API_BASE_URL ||
  process.env.VITE_API_BASE_URL ||
  "https://nouzhaback-theta.vercel.app";

const normalizeId = (maybeId) => {
  if (!maybeId) return null;
  if (typeof maybeId === "string") return maybeId;
  // cas: populate object
  if (typeof maybeId === "object") return maybeId._id || maybeId.$oid || null;
  return null;
};

const normalizeCarObject = (maybeCar) => {
  if (!maybeCar) return null;
  if (typeof maybeCar === "object" && (maybeCar._id || maybeCar.id)) return maybeCar;
  return null;
};

const resolveImageUrl = (img) => {
  if (!img) return "";
  const s = String(img);
  if (s.startsWith("http://") || s.startsWith("https://")) return s;
  if (s.startsWith("/")) return `${API_BASE}${s}`;
  return `${API_BASE}/${s}`;
};

/* ---------------- component ---------------- */
const VoitureReservationAdminModal = ({ reservation, onClose, onUpdated }) => {
  const reservationId = reservation?._id;

  // ✅ DATES selon ton schema (dateDebut/dateFin) + fallback anciens noms
  const dateDebut = pick(reservation, ["dateDebut", "date_debut", "startDate", "dateDepart"], null);
  const dateFin = pick(reservation, ["dateFin", "date_fin", "endDate", "dateRetour"], null);

  // ✅ commentaires selon ton schema (comments) + fallback
  const comments = pick(reservation, ["comments", "commentaires", "commentaire", "message"], "");

  // ✅ Lieu de prise + fallback
  const pickupLocation = pick(reservation, ["pickupLocation", "lieuPrise", "pickup_location"], "");

  // ✅ Nb personnes + fallback
  const nbPersonnes = pick(reservation, ["nbPersonnes", "nb_personnes", "numberOfPeople"], null);

  // ✅ IDs voiture (nouveau + ancien)
  const assignedCarIdRaw = pick(reservation, ["assignedCarId", "assigned_car_id", "voiture", "voitureId", "carId"], null);
  const requestedCarIdRaw = pick(reservation, ["requestedCarId", "requested_car_id", "selectedCarId"], null);

  const assignedCarId = normalizeId(assignedCarIdRaw);
  const requestedCarId = normalizeId(requestedCarIdRaw);

  // ✅ Si backend populate: ça peut être un objet directement
  // Cas 1: reservation.assignedCarId est déjà un objet voiture
  const populatedAssigned = normalizeCarObject(reservation?.assignedCarId);
  // Cas 2: reservation.requestedCarId est déjà un objet voiture
  const populatedRequested = normalizeCarObject(reservation?.requestedCarId);
  // (fallback ancien) reservation.voiture
  const populatedLegacyVoiture = normalizeCarObject(reservation?.voiture);

  // ✅ PRIORITÉ d’affichage : assigned > requested > legacy
  const preferredPopulatedCar =
    populatedAssigned || populatedRequested || populatedLegacyVoiture || null;

  // ✅ ID à charger si pas populate
  const preferredCarIdToLoad = assignedCarId || requestedCarId || null;

  const initialPrice = pick(reservation, ["prixFinal", "prix_total", "prix", "montant"], "");
  const initialPaymentUrl = pick(reservation, ["paymentUrl", "lienPaiement", "payment_link"], "");

  const [prix, setPrix] = useState(initialPrice ? String(initialPrice) : "");
  const [savingPrice, setSavingPrice] = useState(false);

  const [paymentUrl, setPaymentUrl] = useState(initialPaymentUrl || "");
  const [generatingLink, setGeneratingLink] = useState(false);

  // ✅ voiture affichée (assigned ou requested)
  const [shownVoiture, setShownVoiture] = useState(preferredPopulatedCar);
  const [loadingVoiture, setLoadingVoiture] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadCar = async () => {
      try {
        // 1) Si déjà populate (assigned/requested/legacy)
        if (preferredPopulatedCar) {
          setShownVoiture(preferredPopulatedCar);
          return;
        }

        // 2) Sinon charger par ID (assigned prioritaire, sinon requested)
        if (!preferredCarIdToLoad) {
          setShownVoiture(null);
          return;
        }

        setLoadingVoiture(true);

        const res = await adminService.getItemById("voitures", preferredCarIdToLoad);
        const voiture = res?.item || res?.data?.item || res?.data || res;

        if (!cancelled) setShownVoiture(voiture || null);
      } catch (e) {
        console.error("Erreur load voiture:", e);
        if (!cancelled) setShownVoiture(null);
      } finally {
        if (!cancelled) setLoadingVoiture(false);
      }
    };

    loadCar();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    reservationId,
    assignedCarId,
    requestedCarId,
    // repopulate guards
    populatedAssigned?._id,
    populatedRequested?._id,
    populatedLegacyVoiture?._id,
  ]);

  // ---------------------------
  // ✅ Infos client modifiables
  // ---------------------------
  const initialClientName = pick(
    reservation,
    ["client.nomComplet", "client.nom", "nom", "username", "compte.username"],
    ""
  );

  const initialClientPhone = pick(reservation, ["client.telephone", "telephone", "compte.telephone"], "");
  const initialClientAddress = pick(reservation, ["client.adresse", "adresse"], "");
  const initialClientEmailEditable = pick(
    reservation,
    ["client.email", "email", "utilisateur.email", "compte.email"],
    ""
  );

  const [isEditingClient, setIsEditingClient] = useState(false);
  const [savingClient, setSavingClient] = useState(false);

  const [clientName, setClientName] = useState(initialClientName);
  const [clientEmail, setClientEmail] = useState(initialClientEmailEditable);
  const [clientPhone, setClientPhone] = useState(initialClientPhone);
  const [clientAddress, setClientAddress] = useState(initialClientAddress);

  useEffect(() => {
    setPrix(initialPrice ? String(initialPrice) : "");
    setPaymentUrl(initialPaymentUrl || "");

    setClientName(initialClientName);
    setClientEmail(initialClientEmailEditable);
    setClientPhone(initialClientPhone);
    setClientAddress(initialClientAddress);

    setIsEditingClient(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservationId]);

  const resetClientForm = () => {
    setClientName(initialClientName);
    setClientEmail(initialClientEmailEditable);
    setClientPhone(initialClientPhone);
    setClientAddress(initialClientAddress);
  };

  const handleSaveClientInfo = async () => {
    if (!reservationId) return;

    if (clientEmail && !String(clientEmail).includes("@")) {
      return alert("Email invalide");
    }

    try {
      setSavingClient(true);

      const payload = {
        client: {
          nomComplet: clientName || undefined,
          email: clientEmail || undefined,
          telephone: clientPhone || undefined,
          adresse: clientAddress || undefined,
        },
      };

      Object.keys(payload.client).forEach((k) => payload.client[k] === undefined && delete payload.client[k]);

      await adminService.updateItem("reservationVoitures", reservationId, payload);

      setIsEditingClient(false);
      await onUpdated?.();
      alert("Informations client mises à jour ✅");
    } catch (e) {
      console.error(e);
      alert("Erreur lors de la mise à jour des infos client");
    } finally {
      setSavingClient(false);
    }
  };

  // ---------------------------
  // Email
  // ---------------------------
  const defaultMessage = useMemo(() => {
    return `Bonjour,

Nous avons traité votre demande de location de voiture.
Veuillez effectuer le paiement via le lien sécurisé suivant :
[LIEN_PAIEMENT]

Cordialement,
Nouzha Hotel`;
  }, []);

  const [emailSubject, setEmailSubject] = useState("Lien de paiement - Location voiture");
  const [emailMessage, setEmailMessage] = useState(defaultMessage);
  const [sendingEmail, setSendingEmail] = useState(false);

  // --- prix / paiement ---
  const handleSavePrice = async () => {
    const numeric = Number(prix);
    if (!numeric || numeric <= 0) return alert("Prix invalide");

    try {
      setSavingPrice(true);
      if (typeof adminService.updateVoitureReservationPrice === "function") {
        await adminService.updateVoitureReservationPrice(reservationId, numeric);
      } else {
        await adminService.updateItem("reservationVoitures", reservationId, { prixFinal: numeric });
      }
      await onUpdated?.();
      alert("Prix enregistré ✅");
    } catch (e) {
      console.error(e);
      alert("Erreur lors de l'enregistrement du prix");
    } finally {
      setSavingPrice(false);
    }
  };

  const handleGeneratePaymentLink = async () => {
    const numeric = Number(prix);
    if (!numeric || numeric <= 0) return alert("Définis un prix d'abord");

    try {
      setGeneratingLink(true);

      if (typeof adminService.generateVoitureReservationPaymentLink !== "function") {
        return alert("Endpoint payment-link voiture non implémenté côté backend/adminService.");
      }

      const res = await adminService.generateVoitureReservationPaymentLink(reservationId, numeric);
      const url = res?.paymentUrl || res?.data?.paymentUrl || res?.url;
      if (!url) throw new Error("paymentUrl manquant");
      setPaymentUrl(url);
      await onUpdated?.();
      alert("Lien généré ✅");
    } catch (e) {
      console.error(e);
      alert("Erreur lors de la génération du lien");
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleSendEmail = async () => {
    if (!paymentUrl) return alert("Génère d'abord un lien de paiement");
    if (!clientEmail) return alert("Email client introuvable");

    try {
      setSendingEmail(true);

      await adminService.sendVoitureReservationPaymentEmail(reservationId, {
        subject: emailSubject,
        messageTemplate: emailMessage,
        prixFinal: Number(prix) || undefined,
      });

      await onUpdated?.();
      alert("Email envoyé ✅");
    } catch (e) {
      console.error(e);
      alert(e?.message || "Erreur lors de l'envoi email");
    } finally {
      setSendingEmail(false);
    }
  };

  const copyLink = async () => {
    if (!paymentUrl) return;
    await navigator.clipboard.writeText(paymentUrl);
    alert("Lien copié ✅");
  };

  const carLabel = assignedCarId
    ? "Voiture assignée"
    : requestedCarId
      ? "Voiture choisie (client)"
      : "Voiture";

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[80] flex items-center justify-center p-4 pt-20 overflow-y-auto">
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-5xl w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-bold">Détails de la réservation voiture</h3>
              <p className="text-white/90 text-xs">ID: {safe(reservationId)}</p>
            </div>
            <button onClick={onClose} className="text-white hover:text-slate-200">
              ✖
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[70vh] space-y-8">
          {/* Infos demande */}
          <div>
            <h4 className="text-lg font-semibold text-slate-900 border-b pb-2 mb-4">Demande</h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-slate-50 p-4 rounded-xl">
                <div className="text-xs text-slate-500">Lieu de prise</div>
                <div className="font-semibold">{safe(pickupLocation)}</div>
              </div>

              <div className="bg-slate-50 p-4 rounded-xl">
                <div className="text-xs text-slate-500">Nb personnes</div>
                <div className="font-semibold">{safe(nbPersonnes)}</div>
              </div>

              <div className="bg-slate-50 p-4 rounded-xl">
                <div className="text-xs text-slate-500">Date début</div>
                <div className="font-semibold">{formatDate(dateDebut)}</div>
              </div>

              <div className="bg-slate-50 p-4 rounded-xl">
                <div className="text-xs text-slate-500">Date fin</div>
                <div className="font-semibold">{formatDate(dateFin)}</div>
              </div>

              <div className="bg-slate-50 p-4 rounded-xl">
                <div className="text-xs text-slate-500">Statut</div>
                <div className="font-semibold">{safe(reservation?.statut)}</div>
              </div>

              <div className="bg-slate-50 p-4 rounded-xl">
                <div className="text-xs text-slate-500">Créée le</div>
                <div className="font-semibold">{formatDate(reservation?.createdAt)}</div>
              </div>

              <div className="bg-slate-50 p-4 rounded-xl md:col-span-2">
                <div className="text-xs text-slate-500">Commentaires</div>
                <div className="font-semibold">{safe(comments)}</div>
              </div>

              {/* ✅ IDs debug (utile) */}
              <div className="bg-slate-50 p-4 rounded-xl md:col-span-2">
                <div className="text-xs text-slate-500">Voiture (IDs)</div>
                <div className="text-sm">
                  <div>
                    <span className="font-semibold">requestedCarId:</span> {safe(requestedCarId, "—")}
                  </div>
                  <div>
                    <span className="font-semibold">assignedCarId:</span> {safe(assignedCarId, "—")}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Client modifiable */}
          <div>
            <div className="flex items-center justify-between gap-3 border-b pb-2 mb-4">
              <h4 className="text-lg font-semibold text-slate-900">Client</h4>

              {!isEditingClient ? (
                <button
                  onClick={() => setIsEditingClient(true)}
                  className="px-3 py-2 rounded-lg border border-orange-300 text-orange-700 hover:bg-orange-50 transition"
                >
                  ✏️ Modifier
                </button>
              ) : (
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      resetClientForm();
                      setIsEditingClient(false);
                    }}
                    disabled={savingClient}
                    className="px-3 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100 transition disabled:opacity-50"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleSaveClientInfo}
                    disabled={savingClient}
                    className="px-3 py-2 rounded-lg bg-orange-600 text-white hover:bg-orange-700 transition disabled:opacity-50"
                  >
                    {savingClient ? "Sauvegarde..." : "💾 Sauvegarder"}
                  </button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-orange-50 p-4 rounded-xl border border-orange-100">
                <div className="text-xs text-slate-500 mb-1">Nom</div>
                {!isEditingClient ? (
                  <div className="font-semibold">{safe(clientName)}</div>
                ) : (
                  <input
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    className="w-full px-3 py-2 border border-orange-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Nom complet"
                  />
                )}
              </div>

              <div className="bg-orange-50 p-4 rounded-xl border border-orange-100">
                <div className="text-xs text-slate-500 mb-1">Email</div>
                {!isEditingClient ? (
                  <div className="font-semibold text-orange-700">{safe(clientEmail)}</div>
                ) : (
                  <input
                    value={clientEmail}
                    onChange={(e) => setClientEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-orange-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="email@exemple.com"
                  />
                )}
              </div>

              <div className="bg-orange-50 p-4 rounded-xl border border-orange-100">
                <div className="text-xs text-slate-500 mb-1">Téléphone</div>
                {!isEditingClient ? (
                  <div className="font-semibold">{safe(clientPhone)}</div>
                ) : (
                  <input
                    value={clientPhone}
                    onChange={(e) => setClientPhone(e.target.value)}
                    className="w-full px-3 py-2 border border-orange-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="+212..."
                  />
                )}
              </div>

              <div className="bg-orange-50 p-4 rounded-xl border border-orange-100">
                <div className="text-xs text-slate-500 mb-1">Adresse</div>
                {!isEditingClient ? (
                  <div className="font-semibold">{safe(clientAddress)}</div>
                ) : (
                  <input
                    value={clientAddress}
                    onChange={(e) => setClientAddress(e.target.value)}
                    className="w-full px-3 py-2 border border-orange-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Adresse..."
                  />
                )}
              </div>
            </div>
          </div>

          {/* ✅ Voiture (assignée OU choisie) */}
          <div>
            <h4 className="text-lg font-semibold text-slate-900 border-b pb-2 mb-4">{carLabel}</h4>

            {loadingVoiture ? (
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-slate-600">
                Chargement de la voiture...
              </div>
            ) : shownVoiture ? (
              <div className="bg-green-50 p-5 rounded-xl border border-green-100">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                  <div className="md:col-span-2">
                    <div className="text-xs text-slate-500">Véhicule</div>
                    <div className="text-lg font-bold">
                      {safe(shownVoiture.marque)} {safe(shownVoiture.modele)}
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-3">
                      <div>
                        <div className="text-xs text-slate-500">Ville</div>
                        <div className="font-semibold">{safe(shownVoiture.ville)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">Prix/jour</div>
                        <div className="font-semibold">{safe(shownVoiture.prix_jour)} MAD</div>
                      </div>
                    </div>

                    <div className="mt-3 text-xs text-slate-500">ID voiture: {safe(shownVoiture._id)}</div>

                    {/* petit badge pour savoir d'où vient la voiture */}
                    <div className="mt-2">
                      {assignedCarId ? (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-blue-50 text-blue-700 border border-blue-200">
                          Source: assignedCarId (admin)
                        </span>
                      ) : requestedCarId ? (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-orange-50 text-orange-700 border border-orange-200">
                          Source: requestedCarId (client)
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-slate-50 text-slate-700 border border-slate-200">
                          Source: inconnue
                        </span>
                      )}
                    </div>
                  </div>

                  {shownVoiture.image && (
                    <div className="flex justify-center">
                      <img
                        src={resolveImageUrl(shownVoiture.image)}
                        alt="Voiture"
                        className="w-full max-w-[220px] h-28 object-cover rounded-xl border border-slate-200"
                        onError={(e) => (e.currentTarget.style.display = "none")}
                      />
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-slate-600">
                Aucune voiture (ni choisie, ni assignée) pour le moment.
              </div>
            )}
          </div>

          {/* Prix + paiement */}
          <div className="bg-blue-50 p-6 rounded-xl border border-blue-100">
            <h4 className="text-lg font-semibold text-blue-900 mb-4">Tarification & Paiement</h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Prix (MAD)</label>
                <input
                  type="number"
                  value={prix}
                  onChange={(e) => setPrix(e.target.value)}
                  placeholder="Définir le prix final"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  onClick={handleSavePrice}
                  disabled={savingPrice || !prix}
                  className="mt-2 w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {savingPrice ? "Sauvegarde..." : "💾 Sauvegarder le prix"}
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Lien de paiement</label>
                <div className="flex">
                  <input
                    type="text"
                    value={paymentUrl}
                    readOnly
                    placeholder="Générer un lien de paiement"
                    className="w-full px-4 py-3 border border-slate-300 rounded-l-lg bg-slate-50"
                  />
                  <button
                    onClick={copyLink}
                    disabled={!paymentUrl}
                    className="px-4 py-2 bg-slate-200 text-slate-700 rounded-r-lg hover:bg-slate-300 transition-colors disabled:opacity-50"
                    title="Copier"
                  >
                    📋
                  </button>
                </div>
                <button
                  onClick={handleGeneratePaymentLink}
                  disabled={generatingLink || !prix || Number(prix) <= 0}
                  className="mt-2 w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  {generatingLink ? "Génération..." : "🔗 Générer lien paiement"}
                </button>
              </div>
            </div>
          </div>

          {/* Email */}
          <div className="bg-green-50 p-6 rounded-xl border border-green-100">
            <h4 className="text-lg font-semibold text-green-900 mb-4">Envoyer email au client</h4>

            <div className="grid grid-cols-1 gap-3">
              <input
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                className="w-full px-4 py-3 border border-green-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Sujet"
              />

              <textarea
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                className="w-full px-4 py-3 border border-green-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent min-h-[180px] font-mono text-sm"
                placeholder="Message..."
              />

              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="text-xs text-green-800">
                  <p>
                    - Utilise <strong>[LIEN_PAIEMENT]</strong> pour insérer le lien automatiquement
                  </p>
                  <p>
                    - Email destinataire : <strong>{safe(clientEmail)}</strong>
                  </p>
                </div>

                <button
                  onClick={handleSendEmail}
                  disabled={sendingEmail || !paymentUrl}
                  className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  {sendingEmail ? "Envoi..." : "📧 Envoyer email"}
                </button>
              </div>

              {!paymentUrl && (
                <p className="text-sm text-red-600">Générez d’abord un lien de paiement pour pouvoir envoyer l’email.</p>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-slate-50 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-100 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default VoitureReservationAdminModal;
